import cn from 'classnames';
import { Breadcrumbs } from 'hooks/product/use-breadcrumbs';
import Head from 'next/head';
import Link from 'next/link';
import React from 'react';
import { jsonLdScriptProps } from 'react-schemaorg';
import { BreadcrumbList, ListItem } from 'schema-dts';
export interface Props {
  breadcrumbs: Breadcrumbs['breadcrumbList'];
  className?: string;
  dataTest: string;
  isLoading?: boolean;
}

const WORDS_LIMIT = 20;

/**
 * Breadcrumbs component. Displays a list of links to navigate between pages.
 */
const Breadcrumbs: React.FC<Props> = ({
  breadcrumbs,
  className,
  dataTest,
  isLoading,
}) => {
  const generateItemList = (
    breadcrumbs: Breadcrumbs['breadcrumbList']
  ): ListItem[] => {
    const schema: ListItem[] = [];

    breadcrumbs.forEach((item, index) => {
      schema.push({
        '@type': 'ListItem',
        position: index + 1,
        name: item.element,
        item: item.href,
      });
    });

    return schema;
  };

  return (
    <>
      <Head>
        <script
          {...jsonLdScriptProps<BreadcrumbList>({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: generateItemList(breadcrumbs),
          })}
        />
      </Head>
      <nav
        data-test="breadcrumbs-section"
        className={cn(
          className,
          'text-text-secondary flex h-[34px] items-center gap-1 py-2 text-xs'
        )}
      >
        {isLoading ? (
          <div
            className={cn(
              className,
              'bg-grey-600 h-5 w-1/3 animate-pulse rounded-sm opacity-40'
            )}
          />
        ) : (
          breadcrumbs.map(({ element, href, as }, index) => (
            <React.Fragment key={index}>
              {index === breadcrumbs.length - 1 ? (
                <span
                  className="text-primary-main truncate"
                  data-test={dataTest}
                >
                  {element}
                </span>
              ) : (
                <Link
                  key={index}
                  href={href}
                  as={as}
                  passHref
                  data-test={dataTest}
                  className={cn(
                    'whitespace-nowrap',
                    element.length > WORDS_LIMIT ? 'truncate' : ''
                  )}
                >
                  {element}
                </Link>
              )}
              {index < breadcrumbs.length - 1 && (
                <span className="text-secondary-700" data-test={dataTest}>
                  /
                </span>
              )}
            </React.Fragment>
          ))
        )}
      </nav>
    </>
  );
};

export default Breadcrumbs;
